import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
// Loads from /api/portfolios/admin, which (unlike /sharedportfolios) still
// returns archived portfolios — this screen has an Active/Inactive column.
import { deletePortfolio, initializeAdminPortfolios, updatePortfolio } from 'slices/portfolioSlice'
import { ChevronUp, ChevronDown, ArrowUpDown, Trash2, MapPin } from 'lucide-react'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'
import { Sheet } from 'components/ui/sheet'
import { Button } from 'components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import PortfolioEditPopup from 'components/popups/PortfolioEditPopup'
import AltRetailerLogo from 'images/vr-new-retail_3.png'

const formatShortDate = (dateStr: string | undefined) => {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	if (isNaN(d.getTime())) return ''
	const mm = String(d.getMonth() + 1).padStart(2, '0')
	const dd = String(d.getDate()).padStart(2, '0')
	const yy = String(d.getFullYear()).slice(-2)
	return `${mm}/${dd}/${yy}`
}

const headerCells = [
	{ id: 'assetName', label: 'Portfolio', width: '', sortable: true },
	{ id: 'description', label: 'Notes', width: '', sortable: true },
	{ id: 'locations', label: 'Locations', width: 'w-24', sortable: true },
	{ id: 'createdAt', label: 'Created', width: 'w-24', sortable: true },
	{ id: 'updatedAt', label: 'Updated', width: 'w-24', sortable: true },
	{ id: 'isActive', label: 'Status', width: 'w-20', sortable: true },
]

const PortfoliosListCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const { userInfo } = useSelector((state: any) => state.userLogin)
	const userId = userInfo?._id

	const searchInputRef = useRef<HTMLInputElement>(null)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [openSheet, setOpenSheet] = useState(false)
	const [recordForEdit, setRecordForEdit] = useState<any>(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	const [orderBy, setOrderBy] = useState<string | null>(null)
	const [order, setOrder] = useState<'asc' | 'desc'>('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	const {
		entities: portfolioEntities,
		ids: portfolioIds,
		isLoading: loading,
		isInitialized,
		error,
	} = useSelector((state: any) => state.portfolios)
	const records = portfolioIds.map((id: string) => portfolioEntities[id])

	useEffect(() => {
		// @ts-ignore
		dispatch(initializeAdminPortfolios())
	}, [dispatch, isInitialized])

	const handleSort = (columnId: string) => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const filteredRecords = useMemo(() => {
		if (!records || records.length === 0) return []
		if (!searchInputValue) return records
		const lowerValue = searchInputValue.toLowerCase()
		return records.filter(
			(x: any) =>
				(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
				(x.description && x.description.toLowerCase().includes(lowerValue)),
		)
	}, [records, searchInputValue])

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a: any, b: any) => {
			let aVal: string
			let bVal: string
			if (orderBy === 'locations') {
				aVal = (a.locations?.length || 0).toString()
				bVal = (b.locations?.length || 0).toString()
			} else if (orderBy === 'isActive') {
				aVal = a.isActive ? 'active' : 'inactive'
				bVal = b.isActive ? 'active' : 'inactive'
			} else {
				aVal = (a[orderBy] || '').toString().toLowerCase()
				bVal = (b[orderBy] || '').toString().toLowerCase()
			}
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	const SortIcon = ({ columnId }: { columnId: string }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="ml-1 h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="ml-1 h-3 w-3 text-[#0080fe]" />
		)
	}

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInputValue(e.target.value)
		setPage(0)
	}

	const handleEditPortfolio = async (newPortfolio: any) => {
		const portfolioId = newPortfolio._id
		newPortfolio.updateNote = 'Edited Portfolio.'
		// @ts-ignore
		await dispatch(updatePortfolio(newPortfolio))
		// @ts-ignore
		dispatch(initializeAdminPortfolios())
		setOpenSheet(false)
	}

	const handleDeletePortfolio = async () => {
		if (!recordForEdit) return
		const portfolioId = recordForEdit._id
		// @ts-ignore
		await dispatch(deletePortfolio({ portfolioId }))
		// @ts-ignore
		dispatch(initializeAdminPortfolios())
		setOpenSheet(false)
		setShowDeleteConfirm(false)
	}

	const renderContent = () => {
		if (loading) {
			return (
				<div className="flex items-center justify-center py-20">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#0080fe]" />
				</div>
			)
		}

		if (error && (!records || records.length === 0)) {
			return <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-600">{error}</div>
		}

		if (!records || records.length === 0) {
			return (
				<div className="flex flex-col items-center justify-center py-20 text-gray-400">
					<svg className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
					</svg>
					<p className="text-sm">No portfolios found.</p>
				</div>
			)
		}

		return (
			<>
				<div className="max-w-[1080px] mx-auto w-full mt-4 bg-white rounded-lg border border-gray-200 p-5">
					{/* Search bar */}
					<div className="mb-4">
						<input
							ref={searchInputRef}
							type="text"
							placeholder="Search by portfolio name or notes..."
							value={searchInputValue}
							onChange={handleSearch}
							className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe]"
						/>
					</div>

					{/* Table */}
					<div className="rounded-lg border border-gray-200 overflow-x-clip [&>div]:overflow-x-clip">
						<Table className="table-fixed w-full">
							<TableHeader>
								<TableRow className="border-b-2 border-[#0080fe] hover:bg-transparent">
									{headerCells.map(cell => (
										<TableHead
											key={cell.id}
											className={[
												cell.sortable !== false ? 'cursor-pointer select-none' : '',
												'text-gray-700 text-xs font-semibold whitespace-nowrap',
												cell.width || '',
											].join(' ')}
											onClick={cell.sortable !== false ? () => handleSort(cell.id) : undefined}
										>
											<span className="inline-flex items-center">
												{cell.label}
												{cell.sortable !== false && cell.label && <SortIcon columnId={cell.id} />}
											</span>
										</TableHead>
									))}
								</TableRow>
							</TableHeader>
							<TableBody>
								{paginatedRecords.map((portfolio: any) => (
									<TableRow
										key={portfolio._id}
										className="h-7 hover:bg-[#fffbf2] cursor-pointer"
										onClick={() => {
											setRecordForEdit(portfolio)
											setShowDeleteConfirm(false)
											setOpenSheet(true)
										}}
									>
										<TableCell className="py-1 text-sm truncate">{portfolio.assetName}</TableCell>
										<TableCell className="py-1 text-sm truncate">{portfolio.description}</TableCell>
										<TableCell className="py-1 text-sm">{portfolio.locations?.length || 0}</TableCell>
										<TableCell className="py-1 text-sm text-gray-500">{formatShortDate(portfolio.createdAt)}</TableCell>
										<TableCell className="py-1 text-sm text-gray-500">{formatShortDate(portfolio.updatedAt)}</TableCell>
										<TableCell className="py-1 text-sm">
											<span className={portfolio.isActive ? 'text-green-600' : 'text-gray-400'}>
												{portfolio.isActive ? 'Active' : 'Inactive'}
											</span>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>

					{sortedRecords.length > 4 && (
						<TablePagination
							page={page}
							rowsPerPage={rowsPerPage}
							count={sortedRecords.length}
							onPageChange={setPage}
							onRowsPerPageChange={setRowsPerPage}
						/>
					)}
				</div>

				{/* Edit Sheet */}
				<Sheet open={openSheet} onClose={() => setOpenSheet(false)} side="right" className="!w-[550px]">
					{recordForEdit ? (
						<>
							{/* Header */}
							<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
								<div className="min-w-0 flex-1">
									<h2 className="text-lg font-semibold text-gray-900 truncate">{recordForEdit.assetName}</h2>
									{recordForEdit.portfolioType && (
										<p className="text-sm text-gray-500">{recordForEdit.portfolioType}</p>
									)}
									{(recordForEdit.createdAt || recordForEdit.updatedAt) && (
										<div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
											{recordForEdit.createdAt && <span>Created: {formatShortDate(recordForEdit.createdAt)}</span>}
											{recordForEdit.updatedAt && <span>Updated: {formatShortDate(recordForEdit.updatedAt)}</span>}
										</div>
									)}
								</div>
								<button
									type="button"
									onClick={() => setOpenSheet(false)}
									className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors ml-3 flex-shrink-0"
									title="Close (Esc)"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							{/* Body */}
							<div className="flex-1 overflow-y-auto p-5">
								{/* Edit Form */}
								<PortfolioEditPopup editPortfolio={handleEditPortfolio} recordForEdit={recordForEdit} />

								{/* Portfolio Locations */}
								{recordForEdit.locations && recordForEdit.locations.length > 0 && (
									<div className="mt-6 pt-5 border-t border-gray-200">
										<h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
											Locations ({recordForEdit.locations.length})
										</h3>
										<div className="space-y-1.5">
											{recordForEdit.locations.map((item: any, idx: number) => {
												const loc = item.location || item
												return (
													<div
														key={loc._id || idx}
														className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors"
													>
														<Avatar className="h-8 w-8 shrink-0">
															<AvatarImage src={loc.image} />
															<AvatarFallback className="bg-gray-100 text-[10px] text-gray-400">
																{loc.assetName?.[0] || '?'}
															</AvatarFallback>
														</Avatar>
														<div className="min-w-0 flex-1">
															<p className="text-sm font-medium text-gray-800 truncate">{loc.assetName}</p>
															{(loc.city || loc.stateProvince) && (
																<p className="text-xs text-gray-400 truncate">
																	{[loc.city, loc.stateProvince].filter(Boolean).join(', ')}
																</p>
															)}
														</div>
														<MapPin className="h-3.5 w-3.5 text-gray-300 shrink-0" />
													</div>
												)
											})}
										</div>
									</div>
								)}
							</div>

							{/* Footer — Delete */}
							<div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center">
								{showDeleteConfirm ? (
									<div className="flex items-center gap-2">
										<span className="text-sm text-red-600">Delete this portfolio?</span>
										<Button variant="danger" onClick={handleDeletePortfolio}>
											Yes, Delete
										</Button>
										<Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
											Cancel
										</Button>
									</div>
								) : (
									<Button variant="ghost-red" onClick={() => setShowDeleteConfirm(true)}>
										<Trash2 className="w-4 h-4 mr-1" />
										Delete
									</Button>
								)}
							</div>
						</>
					) : (
						<div />
					)}
				</Sheet>
			</>
		)
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">Portfolios</h2>
						{renderContent()}
					</main>
				</div>
			</div>
		</div>
	)
}

export default PortfoliosListCN
